/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

// SOS → SMS to the user's emergency contacts. Wires three EXISTING pieces:
//   - buildEmergencySms (emergencySms.ts) — the ≤160-char body w/ maps link
//   - sendWalkMeHomeSms (walkMeHome/smsClient.ts) — the proven Twilio client
//   - the user's emergencyContacts ({ name, phone })
//
// This is the bridge the SOS path was missing: SOS previously only wrote in-app
// notifications to SafeCircle members and never texted emergency contacts,
// despite the UI/AI claiming it did. Kept as a thin, testable helper so the
// safety-critical SOS state machine (useSOSEvent) only calls one function.

import { buildEmergencySms } from "@/utils/safecircle/emergencySms";
import {
  sendWalkMeHomeSms,
  type SmsSendOutcome,
} from "@/utils/walkMeHome/smsClient";
import { eligibleForSms } from "@/utils/safecircle/contactEligibility";
import type { ProtectedProfile } from "@/types/safecircle";
import type { ContactStatus } from "@/hooks/useEmergencyContacts";

export interface SosSmsContact {
  name: string;
  phone: string;
  /** Two-step opt-in status. Absent = legacy/grandfathered = eligible (see
   *  eligibleForSms). Only explicit pending/declined are skipped. */
  status?: ContactStatus;
}

export interface SendSosSmsInput {
  contacts: SosSmsContact[];
  lat: number;
  lng: number;
  eventShortCode?: string;
  profile?: Pick<ProtectedProfile, "name" | "medicalInfo"> | null;
  includeMedical?: boolean;
  getIdToken: () => Promise<string | null>;
  fetchImpl?: typeof fetch;
}

/** Phone numbers to actually SOS-text: two-step-opt-in ELIGIBLE contacts
 *  (confirmed or legacy/grandfathered — pending/declined are excluded) with a
 *  valid-looking number. 🔴 The eligibility filter is the safety-critical gate;
 *  it grandfathers missing-status so legacy contacts are never dropped. Exported
 *  pure for testing. */
export function validSosPhones(contacts: SosSmsContact[]): string[] {
  return contacts
    .filter((c) => eligibleForSms(c.status))
    .map((c) => (c.phone ?? "").trim())
    .filter((p) => p.length >= 7);
}

const NO_CONTACTS_OUTCOME: SmsSendOutcome = {
  ok: false,
  sent: 0,
  failed: 0,
  results: [],
  errorDetail: "no_emergency_contacts",
};

/**
 * Build the emergency SMS and send it to every emergency contact. Returns the
 * per-recipient outcome (so the SOS UI can show "sent to N of M" honestly).
 * Never throws — failures surface in the outcome, never break the SOS flow.
 */
export async function sendSosSms(input: SendSosSmsInput): Promise<SmsSendOutcome> {
  const to = validSosPhones(input.contacts);
  if (to.length === 0) return NO_CONTACTS_OUTCOME;

  const body = buildEmergencySms({
    lat: input.lat,
    lng: input.lng,
    eventShortCode: input.eventShortCode,
    profile: input.profile,
    includeMedical: input.includeMedical,
  });

  return sendWalkMeHomeSms({
    to,
    body,
    getIdToken: input.getIdToken,
    fetchImpl: input.fetchImpl,
  });
}
